import { useAlerts } from "components/Alerts/AlertProvider";
import { shouldAdvanceWithoutReview } from "components/Submission/advanceWithoutReview";
import {
  Issue,
  ReviewMode,
  SignatureType,
  SubmissionReviewerInput,
  SubmissionStatus,
  SubmissionStepStatus,
  SubmissionStepType,
} from "generated-graphql/graphql";
import { SubmissionReviewerSigner } from "hooks/useSubmissionForm";
import { useMoveToReview } from "hooks/useSubmissionMutations";
import { useTouchSubmissionMutation } from "hooks/useTouchSubmissionMutation";
import { useCallback, useMemo } from "react";

// With nothing to review, sending skips review and lands in processing, so the
// button is relabeled and gated by a confirm.
function useSendGateFlags({
  reviewersToPersistCount,
  signer,
  peerReviewToPersist,
  reviewRequired,
  reviewersCount,
  peerReviewRequired,
  issuesCount,
}: {
  reviewersToPersistCount: number;
  signer: SubmissionReviewerSigner | null;
  peerReviewToPersist: boolean;
  reviewRequired: boolean;
  reviewersCount: number;
  peerReviewRequired: boolean;
  issuesCount: number;
}) {
  const advancesWithoutReview = shouldAdvanceWithoutReview({
    reviewerCount: reviewersToPersistCount,
    hasSigner: !!signer,
    peerReviewRequired: peerReviewToPersist,
    reviewRequired,
  });
  const sendDisabled = useMemo(
    () => (reviewRequired && reviewersCount === 0 && !peerReviewRequired) || issuesCount > 0,
    [reviewRequired, reviewersCount, peerReviewRequired, issuesCount]
  );
  return { advancesWithoutReview, sendDisabled };
}

type SendForReviewInput = {
  moveToReview: ReturnType<typeof useMoveToReview>[0];
  touchStep: ReturnType<typeof useTouchSubmissionMutation>["touchStep"];
  goToNextStep: (step: SubmissionStepType) => void;
  submissionId: string;
  signatureType: SignatureType;
  reviewRequired: boolean;
  peerReviewToPersist: boolean;
  reviewMode: ReviewMode;
  reviewerInputs: SubmissionReviewerInput[];
  alerts: ReturnType<typeof useAlerts>;
  revalidate: () => Promise<Issue[]>;
};

function useSendForReviewHandler({
  moveToReview,
  touchStep,
  goToNextStep,
  submissionId,
  signatureType,
  reviewRequired,
  peerReviewToPersist,
  reviewMode,
  reviewerInputs,
  alerts,
  revalidate,
}: SendForReviewInput) {
  return useCallback(async () => {
    const currentIssues = await revalidate();
    if (currentIssues.length > 0) {
      alerts.error("Resolve the validation issues before sending for review.");
      return;
    }
    try {
      const result = await moveToReview({
        variables: {
          input: {
            id: submissionId,
            signatureType,
            reviewRequired,
            peerReviewRequired: peerReviewToPersist,
            reviewMode,
            reviewers: reviewerInputs,
          },
        },
      });
      if (result.data?.moveToReview) {
        // Sending is what "prepares" the reviewers — there's nothing left to
        // configure once it's routed, so the step completes here rather than
        // waiting on the review/sign outcome (that's Review & Sign's job).
        await touchStep(SubmissionStepType.ReviewerPreparation, SubmissionStepStatus.Completed);
        alerts.success(
          result.data.moveToReview.status === SubmissionStatus.ProcessingSubmission
            ? "No review needed — submission moved to processing."
            : "Sent for review."
        );
        // Carries staff straight into the review/processing view instead of
        // stranding them on a step that's now done.
        goToNextStep(SubmissionStepType.ReviewerPreparation);
      }
    } catch (error) {
      alerts.error("Failed to send for review.", error);
    }
  }, [
    revalidate,
    moveToReview,
    submissionId,
    signatureType,
    reviewRequired,
    peerReviewToPersist,
    reviewMode,
    reviewerInputs,
    touchStep,
    alerts,
    goToNextStep,
  ]);
}

/** Sending for review (or, with nothing to review, advancing straight to processing behind a confirm). */
export function useReviewerPreparationSendHandlers(
  input: SendForReviewInput & {
    peerReviewRequired: boolean;
    reviewersToPersistCount: number;
    signer: SubmissionReviewerSigner | null;
    reviewersCount: number;
    issuesCount: number;
    setAdvanceConfirmOpen: (next: boolean) => void;
  }
) {
  const {
    peerReviewRequired,
    reviewersToPersistCount,
    signer,
    reviewersCount,
    issuesCount,
    setAdvanceConfirmOpen,
    ...sendForReviewInput
  } = input;
  const handleSendForReview = useSendForReviewHandler(sendForReviewInput);

  const { advancesWithoutReview, sendDisabled } = useSendGateFlags({
    reviewersToPersistCount,
    signer,
    peerReviewToPersist: sendForReviewInput.peerReviewToPersist,
    reviewRequired: sendForReviewInput.reviewRequired,
    reviewersCount,
    peerReviewRequired,
    issuesCount,
  });
  const handleSend = advancesWithoutReview
    ? () => setAdvanceConfirmOpen(true)
    : handleSendForReview;

  const handleConfirmAdvance = useCallback(() => {
    setAdvanceConfirmOpen(false);
    void handleSendForReview();
  }, [setAdvanceConfirmOpen, handleSendForReview]);

  return {
    handleSendForReview,
    advancesWithoutReview,
    sendDisabled,
    handleSend,
    handleConfirmAdvance,
  };
}
